import { RunContextApp }            from "../RCApp"
import { StrapiClient, 
       }                            from '@strapi/client'
import axios, 
       { AxiosInstance,
       }                            from "axios"
import { UID }                      from "@strapi/strapi"
import { API_TIMEOUT }              from "../../constants/global"
import { ErrorUtils }               from "framework/utility/ErrorUtils"
import { ANALYTICS_EVENTS }         from "framework/analytics/AnalyticsEvents"

export interface CMSApiResponse<T> {
  error ?: any
  data ?: T
}

export class CMSApiClient {

  private client !: StrapiClient
  private axiosClient : AxiosInstance
  private defaultTimeout : number = API_TIMEOUT.DEFAULT

  constructor(private rc : RunContextApp, private serverUrl : string) {
    // Configure axios client with default timeout
    this.axiosClient = axios.create({
      timeout: this.defaultTimeout
    })

    // Central error path: fire a single API error event for any failed request
    this.axiosClient.interceptors.response.use((response) => {
      return response
    }, (error) => {
      this.rc.analytics.trackEvent(ANALYTICS_EVENTS.SESSION.API_ERROR_RECEIVED, {
        status_code : error?.response?.status ?? error?.status ?? 0,
        error_type  : 'api_error',
        method      : (error?.config?.method ?? 'unknown').toUpperCase()
      })
      return Promise.reject(error)
    })
  }

  // Method to set custom default timeout
  setDefaultTimeout(timeoutMs: number) {
    this.defaultTimeout = timeoutMs
    this.axiosClient.defaults.timeout = timeoutMs
  }

  // Method to get current default timeout
  getDefaultTimeout(): number {
    return this.defaultTimeout
  }

  getClient() {
    return this.client
  }

  init(auth : string) {
    
    // Configure axios client with base URL and auth token
    this.axiosClient.defaults.baseURL = this.serverUrl
    this.axiosClient.defaults.headers.common['Authorization'] = `Bearer ${auth}`
  }

  async login(id : string, password : string) {

    const url = `${this.serverUrl}/auth/local`

    try {

      const loginResp = await this.axiosClient.post(url, {
        identifier: id,
        password: password
      }, {
        timeout: API_TIMEOUT.LOGIN
      })

      return loginResp
  
    } catch (error) {

      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : ${url}\n` + 
        `\nresulted in error : \n`, error)

      return {
              error : ErrorUtils.getApiErrorInfo(error)
            }
    }
  }

  async get<T>(url : string, timeout ?: number) : Promise<CMSApiResponse<T>> {

    try {
      
      // Use axios client directly instead of Strapi client's fetch to avoid error transformation
      const response = await this.axiosClient.get(url, {
        timeout: timeout || this.defaultTimeout
      })

      return response.data as CMSApiResponse<T>

    } catch (error) {
      this.rc.error(this.rc.getName(this), `Error >> GET request to : ${url}\n` + 
      `\nresulted in error : \n`, error)

      return {
        error : ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getBlob(url : string, timeout ?: number) : Promise<Blob> {
    try {
      const response = await this.axiosClient.get(url, {
        responseType: 'blob',
        timeout: timeout || this.defaultTimeout
      })
      return response.data as Blob
    } catch (error) {
      this.rc.error(this.rc.getName(this), `Error >> GET blob request to : ${url}\n` + 
      `\nresulted in error : \n`, error)
      throw error
    }
  }

  async post<T>(url : string, data : any,
                stringify : boolean = true,
                timeout ?: number) : Promise<CMSApiResponse<T>> {

    try {

      const response = await this.axiosClient.post(url, data, {
        timeout: timeout || this.defaultTimeout,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response.data as CMSApiResponse<T>

    } catch (error) {
      this.rc.error(this.rc.getName(this), `Error >> POST request to : ${url}\n` +
      `\nresulted in error : \n`, error)

      return {
        error : ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async put<T>(url : string, data : any,
               timeout ?: number) : Promise<CMSApiResponse<T>> {

    try {

      const response = await this.axiosClient.put(url, data, {
        timeout: timeout || this.defaultTimeout,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response.data as CMSApiResponse<T>

    } catch (error) {
      this.rc.error(this.rc.getName(this), `Error >> PUT request to : ${url}\n` +
      `\nresulted in error : \n`, error)

      return {
        error : ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async delete<T>(url : string, timeout ?: number) : Promise<CMSApiResponse<T>> {
    
    try {
      
      // Use axios client directly instead of Strapi client's fetch to avoid error transformation
      const response = await this.axiosClient.delete(url, {
        timeout: timeout || this.defaultTimeout
      })

      return response.data as CMSApiResponse<T>

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
      `Error >> DELETE request to : ${url}\n` + 
      `\nresulted in error : \n`, error)

      return {
        error : ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async uploadTempImage(blob : Blob, fileName : string, timeout : number = API_TIMEOUT.UPLOAD) {

    const fileInfo = {
      name : fileName,
    }

    const formData = new FormData()
    formData.append('files', blob);
    formData.append('fileInfo', JSON.stringify(fileInfo))
    
    try {
      // Use axios client directly instead of Strapi client's fetch to avoid error transformation
      const response = await this.axiosClient.post('/upload', formData, {
        timeout: timeout,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      return {
        data: response.data
      }
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
      `Error >> Image Upload resulted in error : \n`, error)

      return {
              error : ErrorUtils.getApiErrorInfo(error)
            }
    }
  }

  async updateContentImage(blob : Blob, fileName : string, ref : UID.ContentType, refId : string, field : string, timeout : number = API_TIMEOUT.UPLOAD) {

    const formData = new FormData()
    formData.append('files', blob);
    formData.append('ref', ref);
    formData.append('refId', refId);
    formData.append('field', field);

    try {
      // Use axios client directly instead of Strapi client's fetch to avoid error transformation
      const response = await this.axiosClient.post('/upload', formData, {
        timeout: timeout,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
  
      return {
        data: response.data
      }

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
      `Error >> Image Upload resulted in error : \n`, error)

      return {
              error : ErrorUtils.getApiErrorInfo(error)
            }
    }
  }
}